import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { debounceTime, filter } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { PermissionService } from './core/services/permission.service';
import { LangService, Lang } from './core/services/lang.service';
import { ThemeService } from './core/services/theme.service';
import { VehicleService } from './core/services/vehicle.service';
import { TripService } from './core/services/trip.service';
import { DriverService } from './core/services/driver.service';
import { CustomerService } from './core/services/customer.service';
import { NotificationService, DispatchAlert } from './core/services/notification.service';
import { ChatService } from './core/services/chat.service';
import { PwaUpdateService } from './core/services/pwa-update.service';
import { User } from './core/models/user.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  standalone: false,
  styleUrl: './app.scss',
})
export class App implements OnInit {
  currentUser: User | null = null;
  darkMode   = false; // mirrors ThemeService.dark$ for the template binding
  /** Mobile: off-canvas sidebar state (hamburger in the topbar, ≤1024px). */
  sbOpen = false;
  logoFailed = false;
  pageTitle  = 'Dispatch board';
  currentUrl = '';
  currentLang: Lang = 'en';
  // Live sidebar counts.
  vehicleCount = 0;
  dispatchCount = 0;   // active (non-completed/cancelled) trips
  bookingCount = 0;    // total trips
  driverCount = 0;
  institutionCount = 0;
  billingCount = 0;    // trips with open billing status
  toConfirmCount = 0;  // bookings whose confirmation mail has not gone out
  chatUnread = 0;      // total unread chat messages (Messages nav badge)

  currentSection = 'Operations';

  private pageTitles: Record<string, string> = {
    '/dashboard':    'Dispatch board',
    '/trips':        'Bookings',
    '/customers':    'Patients',
    '/vehicles':     'Fleet & vehicles',
    '/drivers':      'Drivers & staff',
    '/billing':      'Billing & claims',
    '/users/roles':  'Roles & Permissions',
    '/users/new':    'Create User',
    '/users':        'Users',
    '/roles/new':    'Create Role',
    '/roles':        'Roles',
    '/dms':          'Document management',
    '/chat':         'Messages',
    '/vehicle-types': 'Vehicle types',
    // These two were missing, so both showed "Dispatch board".
    '/requirements': 'Special requirements',
    '/settings':     'Settings',
  };

  private sectionMap: Record<string, string> = {
    '/dashboard':  'Operations',
    '/trips':      'Operations',
    '/vehicles':   'Fleet',
    '/drivers':    'Fleet',
    '/customers':  'Customers & Finance',
    '/billing':    'Customers & Finance',
    '/users':      'Administration',
    '/roles':      'Administration',
    '/dms':        'Documents',
    '/chat':       'Operations',
    '/vehicle-types': 'Administration',
    '/requirements': 'Administration',
    '/settings':   'Administration',
  };

  constructor(
    public authService: AuthService,
    public permService: PermissionService,
    public langService: LangService,
    public themeService: ThemeService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private vehicleSvc: VehicleService,
    private tripSvc: TripService,
    private driverSvc: DriverService,
    private customerSvc: CustomerService,
    public notifSvc: NotificationService,
    private chatSvc: ChatService,
    pwaUpdate: PwaUpdateService,
  ) {
    this.langService.init();
    pwaUpdate.init();
  }

  /* ── Live notification bell ───────────────────────────────── */
  notifOpen = false;
  notifAlerts: DispatchAlert[] = [];
  notifUnread = 0;
  notifHasAlert = false;
  /** Fancy in-app alert banners for immediate bookings (rendered in app.html). */
  banners: DispatchAlert[] = [];

  toggleNotif(): void {
    this.notifOpen = !this.notifOpen;
    if (this.notifOpen) this.notifSvc.markAllRead();
  }
  goToNotif(a: DispatchAlert): void {
    this.notifOpen = false;
    if (a.route) { this.router.navigateByUrl(a.route); return; } // chat entries
    this.router.navigate(['/trips'], { queryParams: { highlight: a.tripNumber } });
  }
  /** Click a banner → open the booking. A single banner opens that booking
   *  highlighted; a combined "N new immediate" banner opens the bell so the
   *  dispatcher sees the full list of the new immediate bookings. */
  openBanner(a: DispatchAlert): void {
    this.notifSvc.clearBanners();
    if ((a.count ?? 1) > 1) {
      this.notifOpen = true;
      this.notifSvc.markAllRead();
      this.cdr.detectChanges();
      return;
    }
    this.notifSvc.markAllRead();
    this.router.navigate(['/trips'], { queryParams: { highlight: a.tripNumber } });
  }
  dismissBanner(id: string): void { this.notifSvc.dismissBanner(id); }

  ngOnInit(): void {
    // Seed from the current URL so showChrome is correct on the very first render
    // (before the first NavigationEnd fires, e.g. a hard refresh on /book).
    this.currentUrl = this.router.url;
    this.langService.current$.subscribe(lang => { this.currentLang = lang; });
    this.themeService.dark$.subscribe(dark => { this.darkMode = dark; this.cdr.detectChanges(); });

    // Live dispatch notifications (bell) — connect once a user is present.
    this.notifSvc.alerts$.subscribe(a => { this.notifAlerts = a; this.notifHasAlert = this.notifSvc.hasAlert(); this.cdr.detectChanges(); });
    this.notifSvc.unread$.subscribe(n => { this.notifUnread = n; this.cdr.detectChanges(); });
    // Messages sidebar badge: total unread chat messages (live).
    this.notifSvc.chatUnread$.subscribe(n => { this.chatUnread = n; this.cdr.detectChanges(); });
    this.notifSvc.banners$.subscribe(list => { this.banners = list; this.cdr.detectChanges(); });
    // OS-notification click → route to the highlighted booking.
    this.notifSvc.navigate$.subscribe(tripNumber => {
      this.router.navigate(['/trips'], { queryParams: { highlight: tripNumber } });
    });
    // Chat notification click → open the conversation.
    this.notifSvc.navigateChat$.subscribe(conversationId => {
      this.router.navigate(['/chat', conversationId]);
    });

    // Badges otherwise only refresh on navigation, and sending a confirmation
    // happens on the bookings page without navigating anywhere — so the "Zur
    // Bestätigung" count would sit stale exactly when the dispatcher is watching
    // it (WP1455). Every silent trip event already reaches us here; debounced
    // because a bulk send emits one per booking and a driver walks a job through
    // several statuses in a row, and one catch-up covers the lot.
    this.notifSvc.dispatch$.pipe(debounceTime(600)).subscribe(() => {
      if (this.currentUser) this.loadTripCounts();
    });

    this.authService.currentUser$.subscribe(u => {
      this.currentUser = u;
      if (u) {
        this.notifSvc.connect(u.id);
        this.permService.load().subscribe({
          next: () => { this.loadNavCounts(true); this.cdr.detectChanges(); },
          error: () => this.cdr.detectChanges(),
        });
      } else {
        this.permService.clear();
        this.vehicleCount = this.dispatchCount = this.bookingCount = 0;
        this.driverCount = this.institutionCount = this.billingCount = 0;
        this.toConfirmCount = 0;
      }
    });

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      this.currentUrl = e.urlAfterRedirects;
      this.sbOpen = false; // navigating from the mobile drawer closes it
      // Refresh sidebar counts on navigation so they reflect recent changes.
      if (this.currentUser) this.loadNavCounts();
      const isEditUser = /^\/users\/[^/]+\/edit/.test(this.currentUrl);
      if (isEditUser) { this.pageTitle = 'Edit User'; return; }
      const isEditRole = /^\/roles\/[^/]+\/edit/.test(this.currentUrl);
      if (isEditRole) { this.pageTitle = 'Edit Role'; return; }
      const match = Object.keys(this.pageTitles).find(k => this.currentUrl.startsWith(k));
      this.pageTitle = match ? this.pageTitles[match] : 'Dispatch board';
      const secMatch = Object.keys(this.sectionMap).find(k => this.currentUrl.startsWith(k));
      this.currentSection = secMatch ? this.sectionMap[secMatch] : 'Operations';
    });
  }

  /** When the badges were last refreshed, to coalesce bursts. */
  private lastNavCountsAt = 0;
  private readonly NAV_COUNTS_MIN_GAP_MS = 10_000;

  /** Load the live sidebar counts (only for modules the user can see).
   *  Login and the first NavigationEnd both land within a few hundred ms, which
   *  fired every one of these requests twice; navigating quickly repeated them
   *  again per page. One refresh per 10s is plenty for badge numbers. */
  private loadNavCounts(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastNavCountsAt < this.NAV_COUNTS_MIN_GAP_MS) return;
    this.lastNavCountsAt = now;
    this.loadNavCountsNow();
  }

  private loadNavCountsNow(): void {
    // Messages badge — total unread across the caller's chat inbox (all staff).
    // Suppressed while the chat page is open (they're actively reading).
    if (!this.currentUrl.startsWith('/chat')) {
      this.chatSvc.listConversations().subscribe({
        next: rows => {
          this.notifSvc.setChatUnread(rows.reduce((n, c) => n + (c.unread ?? 0), 0));
          this.cdr.detectChanges();
        },
      });
    } else {
      this.notifSvc.setChatUnread(0);
    }
    if (this.permService.hasAccess('fleet')) {
      this.vehicleSvc.list().subscribe({ next: l => { this.vehicleCount = l.length; this.cdr.detectChanges(); } });
    }
    if (this.permService.hasAccess('drivers')) {
      this.driverSvc.list().subscribe({ next: l => { this.driverCount = l.length; this.cdr.detectChanges(); } });
    }
    if (this.permService.hasAccess('patients')) {
      this.customerSvc.list(undefined, 'institution').subscribe({ next: l => { this.institutionCount = l.length; this.cdr.detectChanges(); } });
    }
    this.loadTripCounts();
  }

  /** Trips power the Dispatch (active), Bookings (total), Billing (open) and
   *  Zur Bestätigung (toConfirm) badges. Counted server-side: pulling 500 trips
   *  per navigation to count them was the heaviest request in the app and helped
   *  exhaust the rate limit. Split out from the rest because a dispatch event
   *  only ever moves these four — refetching vehicles, drivers, customers and
   *  the chat inbox as well would be four wasted requests per event. */
  private loadTripCounts(): void {
    if (!this.permService.hasAccess('dispatch') && !this.permService.hasAccess('bookings') && !this.permService.hasAccess('billing')) return;
    this.tripSvc.counts().subscribe({
      next: c => {
        this.bookingCount = c.total;
        this.dispatchCount = c.active;
        this.billingCount = c.billingOpen;
        this.toConfirmCount = c.toConfirm;
        this.cdr.detectChanges();
      },
      error: () => this.cdr.detectChanges(),
    });
  }

  get userInitials(): string {
    if (!this.currentUser) return 'U';
    return `${this.currentUser.firstName[0] ?? ''}${this.currentUser.lastName[0] ?? ''}`.toUpperCase();
  }
  /** Logged-in user's profile photo, or null to fall back to initials. */
  get userPhoto(): string | null { return this.currentUser?.avatarPhoto || null; }

  isRoute(path: string): boolean { return this.currentUrl.startsWith(path); }
  /** The "Zur Bestätigung" entry points at the Bookings view with a filter, so
   *  both nav items match /trips — without this the dispatcher clicks the inbox
   *  and "Buchungen" lights up instead. The query string is part of currentUrl
   *  (NavigationEnd gives urlAfterRedirects). */
  get onToConfirm(): boolean {
    return this.currentUrl.startsWith('/trips') && this.currentUrl.includes('status=zur_best');
  }
  toggleTheme(): void { this.themeService.toggle(); }

  /** Routes that must render chrome-free (no sidebar/header) even when a staff
   *  user happens to be logged in — the public booking form and the auth pages. */
  private readonly CHROMELESS_ROUTES = ['/book', '/auth'];
  /** Show the app shell (sidebar + header) only for authenticated, non-public
   *  routes. The public form at /book stays standalone regardless of login. */
  get showChrome(): boolean {
    if (this.CHROMELESS_ROUTES.some(r => this.currentUrl.startsWith(r))) return false;
    return this.authService.isLoggedIn;
  }

  logout(): void {
    this.permService.clear();
    this.authService.logout();
  }

  onLogoError(e: Event): void {
    (e.target as HTMLImageElement).style.display = 'none';
    this.logoFailed = true;
  }

  /** Show nav item only if the user has at least VIEW access to the module */
  canSee(module: string): boolean {
    return this.permService.hasAccess(module);
  }

  /** Show action (create/edit) controls only with MANAGE access to the module */
  canManage(module: string): boolean {
    return this.permService.can(module, 'MANAGE');
  }
}
